import { SURVEY_QUESTION_TYPE } from "utils/graphql/Global";
import portalSettingsManager from "utils/portalSettingsManager";
import { IsDateBefore, removeSpaceAndSpecialCharacters } from "utils/helpers";
import { SurveyData_survey_questions } from "utils/graphql/SurveyData";
import sanitize from "sanitize-html";

interface ValidationError {
  showMandatoryError?: boolean;
  showInvalidInputError?: boolean;
  showMinCharError?: boolean;
  showMaxCharError?: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, ValidationError>;
}

export interface QuestionValidationResult {
  isValid: boolean;
  errors: ValidationError;
}

export const validateQuestion = (
  question: SurveyData_survey_questions,
  answer: any,
): QuestionValidationResult => {
  let isValid = true;
  const errors: ValidationError = {};

  if (!question.isMandatory && question.questionType !== SURVEY_QUESTION_TYPE.DateField) {
    return { isValid: true, errors: {} };
  }

  if (!answer) {
    if (question.isMandatory) {
      return { isValid: false, errors: { showMandatoryError: true } };
    }
    return { isValid: true, errors: {} };
  }

  switch (question.questionType) {
    case SURVEY_QUESTION_TYPE.ONLYMCQ:
    case SURVEY_QUESTION_TYPE.Dropdown:
      if (!answer.optionId) {
        errors.showMandatoryError = true;
        isValid = false;
      }
      break;

    case SURVEY_QUESTION_TYPE.Boolean:
      if (answer.value === undefined || answer.value === null || answer.value === "") {
        errors.showMandatoryError = true;
        isValid = false;
      }
      break;

    case SURVEY_QUESTION_TYPE.TextBox:
      if (!answer.value || answer.value.trim() === "") {
        errors.showMandatoryError = true;
        isValid = false;
      }
      break;

    case SURVEY_QUESTION_TYPE.DateField: {
      const dateValue = answer?.value?.trim() ?? "";
      const hasInvalidDate = Boolean(answer?.invalidDate);

      if (hasInvalidDate) {
        errors.showInvalidInputError = true;
        isValid = false;
        break;
      } else if (!dateValue) {
        if (question.isMandatory) {
          errors.showMandatoryError = true;
          isValid = false;
        }
        break;
      }
      break;
    }

    case SURVEY_QUESTION_TYPE.ONLYComment:
    case SURVEY_QUESTION_TYPE.MCQWithComment: {
      const comment = answer.comment || "";
      if (!comment || comment.trim() === "") {
        errors.showMandatoryError = true;
        isValid = false;
      } else {
        const cleanComment = comment.replace(removeSpaceAndSpecialCharacters, "");
        const minChars =
          portalSettingsManager.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMinLength || 0;
        const maxChars =
          portalSettingsManager.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMaxLength ||
          10000;

        if (cleanComment.length < minChars) {
          errors.showMinCharError = true;
          isValid = false;
        }
        if (cleanComment.length > maxChars) {
          errors.showMaxCharError = true;
          isValid = false;
        }
      }
      break;
    }

    case SURVEY_QUESTION_TYPE.MultiSelect:
      if (!answer.selectedValues || answer.selectedValues.length === 0) {
        errors.showMandatoryError = true;
        isValid = false;
      }
      break;
  }

  return { isValid, errors };
};

export const validateAllQuestions = (
  questions: SurveyData_survey_questions[],
  answers: Record<string, any>,
): ValidationResult => {
  let allValid = true;
  const newErrors: Record<string, ValidationError> = {};

  questions.forEach((question) => {
    const answer = answers[question.questionId];
    const { isValid, errors } = validateQuestion(question, answer);

    if (!isValid) {
      newErrors[question.questionId] = errors;
      allValid = false;
    }
  });

  return { isValid: allValid, errors: newErrors };
};

export const mapToResponse = (answers: Record<string, any>) => {
  return Object.entries(answers).map(([questionId, answer]) => {
    let optionId = null;
    let learnerComment = null;

    if (answer.optionId) {
      optionId = sanitize(answer.optionId, {
        allowedTags: [],
        allowedAttributes: {},
      });
    }
    if (answer.comment) {
      learnerComment = sanitize(answer.comment, {
        allowedTags: [],
        allowedAttributes: {},
      });
    }
    if (answer.value) {
      learnerComment = sanitize(answer.value, {
        allowedTags: [],
        allowedAttributes: {},
      });
    }
    if (answer.selectedValues) {
      learnerComment = sanitize(answer.selectedValues.join(","), {
        allowedTags: [],
        allowedAttributes: {},
      });
    }

    return {
      key: questionId,
      value: {
        optionId,
        learnerComment,
      },
    };
  });
};

// Date validation utilities
export const hasTypedValue = (inputValue: string): boolean => {
  return /\d/.test(inputValue);
};

export const hasValidDateFormat = (inputValue: string): boolean => {
  return /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(inputValue);
};

export const hasInvalidDateInput = (inputValue: string, date: Date | null): boolean => {
  return hasTypedValue(inputValue) && (!hasValidDateFormat(inputValue) || !date);
};

const isIsoDateString = (value?: string) => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

export const hasEndDateBeforeStartDate = (startDate?: string, endDate?: string): boolean => {
  if (!isIsoDateString(startDate) || !isIsoDateString(endDate)) {
    return false;
  }

  // IsDateBefore returns true for "before or equal"; equality is valid for this rule.
  return endDate !== startDate && IsDateBefore(endDate, startDate);
};

export const validateDateRange = (
  startDate?: string,
  endDate?: string,
  errorMessage?: string,
): { showInvalidInputError: boolean; customInvalidInputMessage?: string } => {
  if (hasEndDateBeforeStartDate(startDate, endDate)) {
    return { showInvalidInputError: true, customInvalidInputMessage: errorMessage };
  }
  return { showInvalidInputError: false, customInvalidInputMessage: undefined };
};
